import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileCode2, Download, AlertOctagon, FileDown, ArrowRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ConsultCtaBand from "../components/ConsultCtaBand";
import SeverityBadge from "../components/SeverityBadge";
import { PricingAuditsContent } from "./PricingAudits";
import api from "../lib/api";

import useDocumentTitle from "../hooks/useDocumentTitle";

const SAMPLE = `// SPDX-License-Identifier: MIT
pragma solidity ^0.8.20;

contract MockToken {
    bytes32 public constant PERMIT_TYPEHASH =
      keccak256("Permit(address owner,address spender,uint256 value,uint256 nonce,uint256 deadline)");

    address public constant SIGNER = 0x1234567890123456789012345678901234567890;
    mapping(address => uint256) public nonces;

    function permit(
        address owner,
        address spender,
        uint256 value,
        uint256 deadline,
        uint8 v, bytes32 r, bytes32 s
    ) external {
        require(block.timestamp <= deadline, "expired");
        bytes32 digest = keccak256(abi.encode(
            PERMIT_TYPEHASH, owner, spender, value, nonces[owner]++, deadline
        ));
        address recovered = ecrecover(digest, v, r, s);
        require(recovered == owner, "bad sig");
    }
}
`;

const EVM_OPTIONS = [
  { id: "ethereum", label: "Ethereum" },
  { id: "bsc",      label: "BNB Smart Chain" },
  { id: "base",     label: "Base" },
  { id: "polygon",  label: "Polygon" },
  { id: "arbitrum", label: "Arbitrum" },
  { id: "optimism", label: "Optimism" },
];

export default function ContractPage() {
  useDocumentTitle('Contract Analyzer — QorTrace');
  const [mode, setMode] = useState("paste");
  const [chain, setChain] = useState("ethereum");
  const [address, setAddress] = useState("");
  const [name, setName] = useState("Pasted Contract");
  const [source, setSource] = useState(SAMPLE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [quotaLock, setQuotaLock] = useState(null);  // {status, code, message, signup_url|upgrade_url}

  const submit = async (e) => {
    e?.preventDefault();
    setError(""); setQuotaLock(null); setResult(null); setLoading(true);
    try {
      let payload;
      if (mode === "paste") {
        payload = { name, source };
      } else {
        payload = { chain, address, name: address };
      }
      const r = await api.analyzeContract(payload);
      setResult(r);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // 401 → signup required (anon), 402 → quota exhausted (signed-in).
      // Both surface as a structured QuotaLock instead of a flat error.
      if ((status === 401 || status === 402) && detail && typeof detail === "object") {
        setQuotaLock({ status, ...detail });
      } else {
        setError(typeof detail === "string"
          ? detail
          : (detail?.message || err.message || "Analysis failed"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen qt-radial-bg">
      <Header />
      <div className="max-w-[1400px] mx-auto px-6 sm:px-8 py-10">
        <div className="qt-label">QORTRACE CONTRACT</div>
        <h1 className="qt-h2 text-4xl sm:text-5xl mt-2">Smart Contract PQC Audit</h1>
        <p className="mt-3 max-w-2xl" style={{ color: "var(--qt-text-2)" }}>
          Static analysis for Solidity. We detect ECDSA verification, EIP-712 signing,
          ERC-2612 permits, BLS pairing, hardcoded signers, and other PQC migration surfaces — with line numbers and severity-tagged migration steps.
        </p>

        <form onSubmit={submit} className="qt-card mt-8" data-testid="contract-form">
          <div className="flex gap-2 mb-4">
            <ModeButton id="paste"   active={mode === "paste"}   onClick={() => setMode("paste")}>Paste source</ModeButton>
            <ModeButton id="address" active={mode === "address"} onClick={() => setMode("address")}>Fetch by address</ModeButton>
          </div>

          {mode === "paste" ? (
            <div>
              <div className="qt-label mb-2">CONTRACT NAME</div>
              <input className="qt-input mb-3" data-testid="contract-name-input"
                     value={name} onChange={(e) => setName(e.target.value)} />
              <div className="qt-label mb-2">SOLIDITY SOURCE</div>
              <textarea
                data-testid="contract-source-input"
                className="qt-input"
                style={{ minHeight: 320, fontFamily: "JetBrains Mono, monospace", fontSize: 12 }}
                value={source}
                onChange={(e) => setSource(e.target.value)}
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
              <div className="md:col-span-3">
                <div className="qt-label mb-2">CHAIN</div>
                <select data-testid="contract-chain-selector"
                        className="qt-select w-full" value={chain} onChange={(e) => setChain(e.target.value)}>
                  {EVM_OPTIONS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
              <div className="md:col-span-9">
                <div className="qt-label mb-2">CONTRACT ADDRESS (verified on-explorer)</div>
                <input className="qt-input"
                       data-testid="contract-address-input"
                       placeholder="0x…"
                       value={address} onChange={(e) => setAddress(e.target.value)} />
                <div className="qt-mono text-xs mt-2" style={{ color: "var(--qt-text-3)" }}>
                  Requires Etherscan API key in backend .env. Without one, paste source directly.
                </div>
              </div>
            </div>
          )}

          <div className="mt-4 flex items-center gap-2">
            <button type="submit"
                    data-testid="contract-analyze-button"
                    disabled={loading} className="qt-btn qt-btn-primary">
              {loading ? "ANALYZING…" : (<><FileCode2 size={16}/> Run analysis</>)}
            </button>
            {error && (
              <div data-testid="contract-error" className="px-3 py-2 rounded text-sm"
                   style={{ background: "rgba(255,42,42,0.08)", border: "1px solid rgba(255,42,42,0.4)", color: "#FF6E6E" }}>
                <AlertOctagon size={14} className="inline -mt-1 mr-2" />{error}
              </div>
            )}
          </div>
        </form>

        {quotaLock && (
          <div
            data-testid="contract-quota-lock"
            className="mt-6 qt-card"
            style={{
              background: "rgba(255,184,0,0.06)",
              border: "1px solid rgba(255,184,0,0.5)",
            }}
          >
            <div className="qt-label" style={{ color: "#FFB800" }}>
              {quotaLock.status === 401
                ? "SIGN UP TO RUN A CONTRACT ANALYSIS"
                : "CONTRACT ANALYSIS QUOTA EXHAUSTED"}
            </div>
            <div className="text-sm mt-2" style={{ color: "#fff", lineHeight: 1.6 }}>
              {quotaLock.message}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {quotaLock.signup_url && (
                <Link
                  to={quotaLock.signup_url}
                  data-testid="contract-quota-signup"
                  className="qt-btn qt-btn-primary"
                  style={{ fontSize: 13, padding: "0.55rem 1rem" }}
                >
                  Sign up free <ArrowRight size={12} />
                </Link>
              )}
              {quotaLock.upgrade_url && (
                <Link
                  to={quotaLock.upgrade_url}
                  data-testid="contract-quota-upgrade"
                  className={quotaLock.signup_url ? "qt-btn qt-btn-secondary" : "qt-btn qt-btn-primary"}
                  style={{ fontSize: 13, padding: "0.55rem 1rem" }}
                >
                  See upgrade plans
                </Link>
              )}
            </div>
          </div>
        )}

        {result && <ContractResult result={result} />}
      </div>

      {/* Unified Audit Pricing — same content as /pricing/audits, anchored
          at #pricing so devs can deep-link from analyzer results. */}
      <section
        data-testid="contract-audit-pricing-section"
        style={{
          borderTop: "1px solid var(--qt-border)",
          marginTop: "2.5rem",
          background: "rgba(4,9,20,0.4)",
        }}
      >
        <PricingAuditsContent embedded={true} />
      </section>

      <ConsultCtaBand source="contract-bottom-cta" variant="card" maxWidth={1400} />
      <Footer />
    </div>
  );
}

function ModeButton({ active, onClick, children, id }) {
  return (
    <button type="button"
            onClick={onClick}
            data-testid={`mode-${id}`}
            className="qt-btn"
            style={{
              background: active ? "var(--qt-surface-hover)" : "transparent",
              borderColor: active ? "var(--qt-cyan)" : "var(--qt-border-strong)",
              border: "1px solid",
              color: active ? "#fff" : "var(--qt-text-2)",
              fontSize: 13,
            }}>
      {children}
    </button>
  );
}

function ContractResult({ result }) {
  const findings = result.findings || [];
  const summary = result.summary || {};
  const advisor = result.advisor || {};
  return (
    <div className="mt-10" data-testid="contract-result">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
        <Stat label="VERDICT"  value={result.verdict} accent={verdictColor(result.verdict)} colSpan={3} />
        <Stat label="CRITICAL" value={summary.Critical || 0} accent="#FF2A2A" colSpan={2} />
        <Stat label="HIGH"     value={summary.High || 0}     accent="#FF8000" colSpan={2} />
        <Stat label="MEDIUM"   value={summary.Medium || 0}   accent="#FFB703" colSpan={2} />
        <Stat label="INFO"     value={summary.Info || 0}     accent="#9CAEC9" colSpan={2} />
        <div className="md:col-span-1 flex">
          <Link to={`/report/${result.id}`} className="qt-btn qt-btn-primary w-full justify-center"
                data-testid="contract-open-report" style={{ fontSize: 12 }}>
            <FileDown size={14}/> PDF
          </Link>
        </div>
      </div>

      <div className="qt-card mt-6">
        <div className="qt-label">FINDINGS · {findings.length}</div>
        {findings.length === 0 ? (
          <div className="mt-4 text-sm" style={{ color: "var(--qt-text-2)" }}>
            No PQC-relevant patterns detected in {result.loc} lines of source.
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {findings.map((f, i) => (
              <div key={i} className="qt-card" style={{ padding: 14, background: "rgba(4,9,20,0.5)" }}>
                <div className="flex items-center gap-3 flex-wrap">
                  <SeverityBadge severity={f.severity} />
                  <span className="font-semibold text-sm">{f.label}</span>
                  <span className="qt-mono text-xs" style={{ color: "var(--qt-text-3)" }}>L{f.line}</span>
                </div>
                <div className="text-sm mt-2" style={{ color: "var(--qt-text-2)" }}>{f.description}</div>
                <pre className="mt-2 px-3 py-2 rounded qt-mono text-xs"
                     style={{ background: "rgba(4,9,20,0.85)", border: "1px solid var(--qt-border)", color: "#9CAEC9", overflowX: "auto" }}>
                  {f.snippet}
                </pre>
                <div className="text-xs mt-2"
                     style={{ color: "var(--qt-cyan)" }}>
                  <span className="qt-label" style={{ color: "var(--qt-cyan)" }}>MIGRATION ›</span> {f.migration}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {advisor && (advisor.executive_summary || advisor.migration_playbook) && (
        <div className="qt-card mt-6">
          <div className="qt-label">ADVISOR · GROUNDED NARRATIVE</div>
          {advisor.executive_summary && (
            <p className="mt-3 text-base" style={{ color: "#fff", lineHeight: 1.6 }}>{advisor.executive_summary}</p>
          )}
          {advisor.migration_playbook?.length > 0 && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
              {advisor.migration_playbook.map((s) => (
                <div key={s.step} className="qt-card" style={{ padding: 14, background: "rgba(4,9,20,0.5)" }}>
                  <div className="qt-mono text-xs" style={{ color: "var(--qt-cyan)" }}>STEP {s.step}</div>
                  <div className="mt-1 font-semibold text-sm">{s.title}</div>
                  <div className="text-xs mt-1.5" style={{ color: "var(--qt-text-2)", lineHeight: 1.5 }}>{s.detail}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent, colSpan = 2 }) {
  return (
    <div className={`qt-card md:col-span-${colSpan}`} style={{ padding: 14, background: "rgba(4,9,20,0.5)" }}>
      <div className="qt-label">{label}</div>
      <div className="qt-mono mt-1" style={{ color: accent || "#fff", fontSize: 22, fontWeight: 700, letterSpacing: "-0.04em" }}>
        {value}
      </div>
    </div>
  );
}

function verdictColor(v) {
  return { Critical: "#FF2A2A", High: "#FF8000", Moderate: "#FFD60A", Low: "#1FDD83" }[v] || "#fff";
}
